import { useMutation, useQuery } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';
import type { IPostcode } from '../Interfaces/IPostcode';
import { IAdminstrativeUnit } from '../Interfaces/IAdminstrativeUnit';
import { IFederalState, ICourtCourtRole } from '../Interfaces/IAemter';
import { ICourt } from '../Interfaces/ICourt';
import { IAuthority } from '../Interfaces/IAuthority';
import {
  IMunicipality,
  IAdminUnitMunicipality,
} from '../Interfaces/IMunicipality';
import { ICourtAdminstrativeUnitAssignment } from '../Interfaces/ICourtAdminstrativeUnitAssignment';
import { ICourtRole } from '../Interfaces/ICourtRole';

export const useAdministrativeUnits = () => {
  const {
    data: administrativeUnits,
    isLoading: isLoadingAdministrativeUnits,
    refetch: refetchAdministrativeUnits,
  } = useQuery<IAdminstrativeUnit[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/administrativeunit'],
    queryFn: () => customFetcher({ endpoint: '/administrativeunit' }),
  });

  return {
    administrativeUnits,
    isLoadingAdministrativeUnits,
    refetchAdministrativeUnits,
  };
};

export const useFederalStates = () => {
  const {
    data: federalStates,
    isLoading: isLoadingFederalStates,
    refetch: refetchFederalStates,
  } = useQuery<IFederalState[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/federalstate'],
    queryFn: () => customFetcher({ endpoint: '/federalstate' }),
  });

  return { federalStates, isLoadingFederalStates, refetchFederalStates };
};

export const useCourtCourtRoles = () => {
  const {
    data: courtCourtRoles,
    isLoading: isLoadingCourtCourtRoles,
    refetch: refetchCourtCourtRoles,
  } = useQuery<ICourtCourtRole[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/courtcourtrole'],
    queryFn: () => customFetcher({ endpoint: '/courtcourtrole' }),
  });

  return { courtCourtRoles, isLoadingCourtCourtRoles, refetchCourtCourtRoles };
};

export const useCourtAdministrativeUnitAssignments = () => {
  const {
    data: courtAdministrativeUnitAssignments,
    isLoading: isLoadingCourtAdministrativeUnitAssignments,
    refetch: refetchCourtAdministrativeUnitAssignments,
  } = useQuery<ICourtAdminstrativeUnitAssignment[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/courtadminstrativeunitassignment'],
    queryFn: () =>
      customFetcher({ endpoint: '/courtadminstrativeunitassignment' }),
  });

  return {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
    refetchCourtAdministrativeUnitAssignments,
  };
};

export const useCourtRoles = () => {
  const {
    data: courtRoles,
    isLoading: isLoadingCourtRoles,
    refetch: refetchCourtRoles,
  } = useQuery<ICourtRole[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/courtrole'],
    queryFn: () =>
      customFetcher({
        endpoint: '/courtrole',
      }),
  });

  return { courtRoles, isLoadingCourtRoles, refetchCourtRoles };
};

export const usePostCodes = () => {
  const { data: postcodes, isLoading: isLoadingPostcodes } = useQuery<
    IPostcode[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['postcode'],
    queryFn: () =>
      customFetcher({
        endpoint: '/postcode',
      }),
  });

  return { postcodes, isLoadingPostcodes };
};

export const useSearchPostcodes = ({
  searchTerm,
  initialData,
}: {
  searchTerm: string;
  initialData?: IPostcode[];
}) => {
  const { data: searchPostcodes, isLoading: isLoadingSearchPostcodes } =
    useQuery<IPostcode[]>({
      refetchOnWindowFocus: false,
      queryKey: ['postcode', searchTerm],
      queryFn: () =>
        customFetcher({
          endpoint: `/postcode/public/${searchTerm}`,
        }),
      enabled: !!searchTerm && searchTerm.length > 1,
      initialData: initialData || [],
    });

  return { searchPostcodes, isLoadingSearchPostcodes };
};

export const useCourts = () => {
  const {
    data: courts,
    isLoading: isLoadingCourts,
    refetch: refetchCourts,
  } = useQuery<ICourt[]>({
    refetchOnWindowFocus: false,
    queryKey: ['court'],
    queryFn: () =>
      customFetcher({
        endpoint: '/court',
      }),
  });

  return { courts, isLoadingCourts, refetchCourts };
};

/*export const useSearchCourts = ({
  searchTerm,
  initialData,
}: {
  searchTerm: string;
  initialData?: IPostcode[];
}) => {
  const { data: searchPostcodes, isLoading: isLoadingSearchPostcodes } =
    useQuery<IPostcode[]>({
      refetchOnWindowFocus: false, queryKey: ['court', searchTerm],
      queryFn: () =>
        customFetcher({
          endpoint: `/postcode/public/${searchTerm}`,
        }),
      enabled: !!searchTerm && searchTerm.length > 1,
      initialData: initialData || [],
    });

  return { searchPostcodes, isLoadingSearchPostcodes };
};*/

export const useDeleteCourtRole = () => {
  const { mutateAsync: deleteCourtRole, isPending: isPendingDeleteCourtRole } =
    useMutation({
      mutationFn: (id: number) =>
        customFetcher({
          endpoint: `/courtrole/${id}`,
          method: 'DELETE',
        }),
    });

  return { deleteCourtRole, isPendingDeleteCourtRole };
};

export const useDeleteAdministrativeUnit = () => {
  const { mutateAsync: deleteAdminUnit, isPending: isPendingDeleteAdminUnit } =
    useMutation({
      mutationFn: (id: number) =>
        customFetcher({
          endpoint: `/administrativeunit/${id}`,
          method: 'DELETE',
        }),
    });

  return { deleteAdminUnit, isPendingDeleteAdminUnit };
};

export const useCreateCourtAdminUnitAssignment = () => {
  const {
    mutateAsync: createCourtAdminUnitAssignment,
    isPending: isPendingCreateAdminUnitAssignment,
  } = useMutation({
    mutationFn: (assignment: ICourtAdminstrativeUnitAssignment) =>
      customFetcher({
        endpoint: '/courtadminstrativeunitassignment',
        method: 'POST',
        body: assignment,
      }),
  });

  return { createCourtAdminUnitAssignment, isPendingCreateAdminUnitAssignment };
};

export const useUpdateCourtAdminUnitAssignment = () => {
  const {
    mutateAsync: updateCourtAdminUnitAssignment,
    isPending: isPendingUpdateCourtAdminUnitAssignment,
  } = useMutation({
    mutationFn: (assignment: ICourtAdminstrativeUnitAssignment) =>
      customFetcher({
        endpoint: '/courtadminstrativeunitassignment',
        method: 'PUT',
        body: assignment,
      }),
  });

  return {
    updateCourtAdminUnitAssignment,
    isPendingUpdateCourtAdminUnitAssignment,
  };
};

export const useUpdateCourtAdminUnitAssignments = () => {
  const {
    mutateAsync: updateCourtAdminUnitAssignments,
    isPending: isPendingUpdateCourtAdminUnitAssignments,
  } = useMutation({
    mutationFn: (assignments: ICourtAdminstrativeUnitAssignment[]) =>
      customFetcher({
        endpoint: '/courtadminstrativeunitassignment/updateObjects',
        method: 'PUT',
        body: assignments,
      }),
  });

  return {
    updateCourtAdminUnitAssignments,
    isPendingUpdateCourtAdminUnitAssignments,
  };
};

export const useDeleteCourtAdminUnitAssignment = () => {
  const { mutateAsync: deleteCourtAdminstrativeUnitAssignment, isPending } =
    useMutation({
      mutationFn: (assignment: ICourtAdminstrativeUnitAssignment) =>
        customFetcher({
          endpoint: `/courtadminstrativeunitassignment/${assignment.idAssignment}`,
          method: 'DELETE',
        }),
    });

  return { deleteCourtAdminstrativeUnitAssignment, isPending };
};

export const useDeleteCourtCourtRoleAssignment = () => {
  const {
    mutateAsync: deleteCourtCourtRoleAssignment,
    isPending: isPendingDeleteCourtCourtRoleAssignment,
  } = useMutation({
    mutationFn: (courtRole: ICourtCourtRole) =>
      customFetcher({
        endpoint: `/courtcourtrole/${courtRole.idCourtRole}/${courtRole.idCourt}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteCourtCourtRoleAssignment,
    isPendingDeleteCourtCourtRoleAssignment,
  };
};

export const useDeleteCourt = () => {
  const { mutateAsync: deleteCourt, isPending: isPendingDeleteCourt } =
    useMutation({
      mutationFn: (courtId: number) =>
        customFetcher({
          endpoint: `/court/${courtId}`,
          method: 'DELETE',
        }),
    });

  return { deleteCourt, isPendingDeleteCourt };
};

// ===== Behörden (Authority) – #1208 =====
export const useAuthorities = () => {
  const {
    data: authorities,
    isLoading: isLoadingAuthorities,
    refetch: refetchAuthorities,
  } = useQuery<IAuthority[]>({
    refetchOnWindowFocus: false,
    queryKey: ['authority'],
    queryFn: () => customFetcher({ endpoint: '/authority' }),
  });

  return { authorities, isLoadingAuthorities, refetchAuthorities };
};

export const useCreateAuthority = () => {
  const { mutateAsync: createAuthority, isPending: isPendingCreateAuthority } =
    useMutation({
      mutationFn: (authority: IAuthority) =>
        customFetcher({
          endpoint: '/authority',
          method: 'POST',
          body: authority,
        }),
    });

  return { createAuthority, isPendingCreateAuthority };
};

export const useUpdateAuthority = () => {
  const { mutateAsync: updateAuthority, isPending: isPendingUpdateAuthority } =
    useMutation({
      mutationFn: (authority: IAuthority) =>
        customFetcher({
          endpoint: '/authority',
          method: 'PUT',
          body: authority,
        }),
    });

  return { updateAuthority, isPendingUpdateAuthority };
};

export const useDeleteAuthority = () => {
  const { mutateAsync: deleteAuthority, isPending: isPendingDeleteAuthority } =
    useMutation({
      mutationFn: (authorityId: number) =>
        customFetcher({
          endpoint: `/authority/${authorityId}`,
          method: 'DELETE',
        }),
    });

  return { deleteAuthority, isPendingDeleteAuthority };
};

// ===== Politische Gemeinde (Municipality) + Mapping – #1207 =====
export const useMunicipalities = () => {
  const {
    data: municipalities,
    isLoading: isLoadingMunicipalities,
    refetch: refetchMunicipalities,
  } = useQuery<IMunicipality[]>({
    refetchOnWindowFocus: false,
    queryKey: ['municipality'],
    queryFn: () => customFetcher({ endpoint: '/municipality' }),
  });

  return { municipalities, isLoadingMunicipalities, refetchMunicipalities };
};

export const useCreateMunicipality = () => {
  const {
    mutateAsync: createMunicipality,
    isPending: isPendingCreateMunicipality,
  } = useMutation({
    mutationFn: (municipality: IMunicipality) =>
      customFetcher({
        endpoint: '/municipality',
        method: 'POST',
        body: municipality,
      }),
  });

  return { createMunicipality, isPendingCreateMunicipality };
};

export const useDeleteMunicipality = () => {
  const {
    mutateAsync: deleteMunicipality,
    isPending: isPendingDeleteMunicipality,
  } = useMutation({
    mutationFn: (municipalityId: number) =>
      customFetcher({
        endpoint: `/municipality/${municipalityId}`,
        method: 'DELETE',
      }),
  });

  return { deleteMunicipality, isPendingDeleteMunicipality };
};

export const useAdminUnitMunicipalities = () => {
  const {
    data: adminUnitMunicipalities,
    isLoading: isLoadingAdminUnitMunicipalities,
    refetch: refetchAdminUnitMunicipalities,
  } = useQuery<IAdminUnitMunicipality[]>({
    refetchOnWindowFocus: false,
    queryKey: ['adminunitmunicipality'],
    queryFn: () => customFetcher({ endpoint: '/adminunitmunicipality' }),
  });

  return {
    adminUnitMunicipalities,
    isLoadingAdminUnitMunicipalities,
    refetchAdminUnitMunicipalities,
  };
};

export const useCreateAdminUnitMunicipality = () => {
  const {
    mutateAsync: createAdminUnitMunicipality,
    isPending: isPendingCreateAdminUnitMunicipality,
  } = useMutation({
    mutationFn: (mapping: IAdminUnitMunicipality) =>
      customFetcher({
        endpoint: '/adminunitmunicipality',
        method: 'POST',
        body: mapping,
      }),
  });

  return {
    createAdminUnitMunicipality,
    isPendingCreateAdminUnitMunicipality,
  };
};

export const useDeleteAdminUnitMunicipality = () => {
  const {
    mutateAsync: deleteAdminUnitMunicipality,
    isPending: isPendingDeleteAdminUnitMunicipality,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/adminunitmunicipality/${id}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteAdminUnitMunicipality,
    isPendingDeleteAdminUnitMunicipality,
  };
};

export const useCreateCourtRole = () => {
  const { isPending: isPendingCreateCourtRole, mutateAsync: createCourtRole } =
    useMutation({
      mutationFn: (courtRole: ICourtRole) =>
        customFetcher({
          endpoint: '/courtrole',
          method: 'POST',
          body: courtRole,
        }),
    });

  return { isPendingCreateCourtRole, createCourtRole };
};

export const useUpdateFederalState = () => {
  const {
    isPending: isPendingUpdateFederalState,
    mutateAsync: updateFederalState,
  } = useMutation({
    mutationFn: (updatedFederalState: IFederalState) =>
      customFetcher({
        endpoint: '/federalstate',
        method: 'PUT',
        body: updatedFederalState,
      }),
  });

  return { isPendingUpdateFederalState, updateFederalState };
};

export const useUpdateCourt = () => {
  const { isPending: isPendingUpdateCourt, mutateAsync: updateCourt } =
    useMutation({
      mutationFn: (court: ICourt) =>
        customFetcher({
          endpoint: '/court',
          method: 'PUT',
          body: court,
        }),
    });

  return { isPendingUpdateCourt, updateCourt };
};

export const useCreateCourt = () => {
  const { isPending: isPendingCreateCourt, mutateAsync: createCourt } =
    useMutation({
      mutationFn: (court: ICourt) =>
        customFetcher({
          endpoint: '/court',
          method: 'POST',
          body: court,
        }),
    });

  return { isPendingCreateCourt, createCourt };
};

export const useCreateCourtCourtRoleAssignment = () => {
  const {
    isPending: isPendingCreateCourtCourtRoleAssignment,
    mutateAsync: createCourtCourtRoleAssignment,
  } = useMutation({
    mutationFn: (courtRole: ICourtCourtRole) =>
      customFetcher({
        endpoint: '/courtcourtrole',
        method: 'POST',
        body: courtRole,
      }),
  });

  return {
    isPendingCreateCourtCourtRoleAssignment,
    createCourtCourtRoleAssignment,
  };
};

export const useCreateCourtCourtRoleAssignments = () => {
  const {
    isPending: isPendingCreateCourtCourtRoleAssignments,
    mutateAsync: createCourtCourtRoleAssignments,
  } = useMutation({
    mutationFn: (courtRole: ICourtCourtRole[]) =>
      customFetcher({
        endpoint: '/courtcourtrole/updateObjects',
        method: 'POST',
        body: courtRole,
      }),
  });

  return {
    isPendingCreateCourtCourtRoleAssignments,
    createCourtCourtRoleAssignments,
  };
};

export const useUpdateCourtCourtRoleAssignment = () => {
  const {
    isPending: isPendingUpdateCourtCourtRoleAssignment,
    mutateAsync: updateCourtCourtRoleAssignment,
  } = useMutation({
    mutationFn: (courtRole: ICourtCourtRole) =>
      customFetcher({
        endpoint: '/courtcourtrole',
        method: 'PUT',
        body: courtRole,
      }),
  });

  return {
    isPendingUpdateCourtCourtRoleAssignment,
    updateCourtCourtRoleAssignment,
  };
};

export const useUpdateAdminUnit = () => {
  const { isPending: isPendingUpdateAdminUnit, mutateAsync: updateAdminUnit } =
    useMutation({
      mutationFn: (updatedAdminUnit: IAdminstrativeUnit) =>
        customFetcher({
          endpoint: '/administrativeunit',
          method: 'PUT',
          body: updatedAdminUnit,
        }),
    });

  return { isPendingUpdateAdminUnit, updateAdminUnit };
};

export const useCreateAdminUnit = () => {
  const { isPending: isPendingCreateAdminUnit, mutateAsync: createAdminUnit } =
    useMutation({
      mutationFn: (adminUnit: IAdminstrativeUnit) =>
        customFetcher({
          endpoint: '/administrativeunit',
          method: 'POST',
          body: adminUnit,
        }),
    });

  return { isPendingCreateAdminUnit, createAdminUnit };
};
